import { z } from 'zod'
import { API_TOKEN_SCOPES, apiTokenScopeSchema, type ApiTokenScope } from './api-token-scope.enum.js'

/**
 * The wire spelling of a grant: `events:read`, `venues:write`.
 *
 * Derived from {@link ApiTokenScope} so a scope added to the enum is a scope
 * on the wire, and a string that is not one of those cannot be stored, shown,
 * or turned into a grant by asserting it. Read versus write is the action; the
 * area is the enum, lowercased, which is the spelling connectors request.
 */
export type OAuthScope = `${Lowercase<ApiTokenScope>}:${'read' | 'write'}`

const OAUTH_SCOPE_VALUES: readonly string[] = API_TOKEN_SCOPES.flatMap((scope) => [
  `${scope.toLowerCase()}:read`,
  `${scope.toLowerCase()}:write`,
])

const OAUTH_SCOPE_SET: ReadonlySet<string> = new Set(OAUTH_SCOPE_VALUES)

/** True when `value` is a scope this server can grant. */
export function isOAuthScope(value: string): value is OAuthScope {
  return OAUTH_SCOPE_SET.has(value)
}

/**
 * A scope on the wire.
 *
 * Failure is a schema issue, not a thrown `TypeError`: a transform that throws
 * escapes `safeParse` and becomes a 500 on the way in, or a response the browser
 * cannot attribute to a field.
 */
export const oauthScopeSchema: z.ZodType<OAuthScope> = z.string().transform((value, ctx) => {
  if (!isOAuthScope(value)) {
    ctx.addIssue({ code: 'custom', message: 'Not a scope this server offers.' })
    return z.NEVER
  }
  return value
})

/**
 * The wire form of one grant.
 *
 * The set this checks against is built from the same enum, so a miss means the
 * two drifted and the process should stop rather than hand a string onward.
 */
export function oauthScope(scope: ApiTokenScope, action: 'read' | 'write'): OAuthScope {
  const value = `${scope.toLowerCase()}:${action}`
  if (!isOAuthScope(value)) {
    throw new Error(`Scope ${scope} has no ${action} wire form`)
  }
  return value
}

/** The area and whether the action is write. `events:write` is `EVENTS` with write set. */
export function oauthScopeParts(scope: OAuthScope): { scope: ApiTokenScope; write: boolean } {
  const separator = scope.lastIndexOf(':')
  const name = scope.slice(0, separator)
  const action = scope.slice(separator + 1)
  return {
    scope: apiTokenScopeSchema.parse(name.toUpperCase()),
    write: action === 'write',
  }
}

/**
 * Scopes read back from a `string[]` column.
 *
 * The column cannot say which strings are scopes. Anything this build does not
 * know is dropped, which is the same rule as a connector naming a scope some
 * other server has: it is asking for less than it thinks, and that is not a grant.
 */
export function oauthScopesFromStrings(values: readonly string[]): OAuthScope[] {
  return values.filter(isOAuthScope)
}
